import { z } from 'zod';
import { LEVELS, ROLES, CATEGORIES, DEFINITION_KINDS, define } from '../types.js';

/**
 * Move types that support appraisals.
 */
export const AppraisalMoveTypes = ['concierge', 'operations'];

/**
 * Timestamp range for appraisal activation.
 */
export const AppraisalTimestampSchema = z
  .object({
    from: z
      .string()
      .datetime({ offset: true })
      .describe('Start date in ISO 8601 format (e.g., "2025-01-01T00:00:00Z")')
      .optional(),
    to: z
      .union([z.string().datetime({ offset: true }), z.literal('now')])
      .describe('End date in ISO 8601 format, or "now" to indicate currently active')
      .optional(),
  })
  .describe(
    'Timestamp range for appraisal activation. Toggle on sets "from" to month start; toggle off sets "to" to month end.'
  );

export const AppraisalMoveTypeSchema = z.enum(AppraisalMoveTypes as [string, ...string[]]);

export type AppraisalTimestamp = z.infer<typeof AppraisalTimestampSchema>;

/**
 * Vehicle appraisals configuration.
 */
export const schema = z
  .object({
    active: z
      .boolean()
      .describe(
        'Activate/Deactivate the appraisals feature for this client. NOTE: Flipping this switch ON will create an active timeframe starting at the beginning of the month. Flipping this switch OFF will end the current active timeframe at the end of the month.'
      )
      .optional(),
    active_timestamps: z
      .array(AppraisalTimestampSchema)
      .describe(
        'Active timeframes are a set of start and end timestamps that determine a window of vehicles for a client to appraise. We do this to restrict clients from being able to access moves they didnt pay the appraisals subscription for. NOTE: The appraisals feature must be activated in order for the client to appraise vehicles (even if they have a time window that includes the current date).'
      )
      .optional(),
    allowed_move_types: z
      .array(AppraisalMoveTypeSchema)
      .describe(
        'Move types that support appraisals. Only these types will have appraisal functionality. Generally, we should allow all move types unless specified in the client contract.'
      )
      .optional(),
    level: z
      .number()
      .int()
      .min(0)
      .describe('Appraisal level/tier (0 = default). Higher levels may unlock additional features.')
      .optional(),
  })
  .strict()
  .describe(
    'The appraisals module is a way for our clients to appraise vehicles right off the lot. This system is powered by the Blackbook API and allows the client to build their own appraisals based on vehicle trim and condition.'
  );

export type Appraisals = z.infer<typeof schema>;

export const definition = define({
  key: 'appraisals',
  schema,
  label: 'Appraisals',
  description:
    'The appraisals module is a way for our clients to appraise vehicles right off the lot. This system is powered by the Blackbook API and allows the client to build their own appraisals based on vehicle trim and condition.',
  warning: 'Requires premium subscription. Check client contract before activating.',
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.APPRAISALS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.RATERULEGROUP],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    active: false,
    active_timestamps: [],
    allowed_move_types: AppraisalMoveTypes,
    level: 0,
  },
});
